import dayjs from 'dayjs'
import { Box, Link, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Services } from '../../../../../store/slices/timesheet/timesheetPageSlice'

const body2TextStyle = {
	display: 'grid',
	gridTemplateColumns: '1fr 34px 72px',
	justifyContent: 'start',
	alignItems: 'baseline',
	gap: 1,
	fontFamily: 'var(--font-family)',
	fontWeight: 400,
	fontSize: '12px',
	lineHeight: '130%',
	letterSpacing: '0.17px',
	color: 'var(--primary-dark)',
}

interface ServiceTimeDetailsProps {
	serviceDetail: Services
	isReserveService: boolean
	isTowDriversService: boolean
	isContinuousService: boolean
	isSecondaryDriverService: boolean
	currentServiceDay?: number
	totalServiceDays?: number
	continuedServiceStartDate?: string | null
	continuedServiceEndDate?: string | null
}

export default function ServiceTimeDetails({
	serviceDetail,
	isReserveService,
	isTowDriversService,
	isContinuousService,
	isSecondaryDriverService,
	currentServiceDay,
	totalServiceDays,
	continuedServiceStartDate,
	continuedServiceEndDate,
}: Readonly<ServiceTimeDetailsProps>) {
	return (
		<Box
			sx={{
				minWidth: '320px',
				width: 'stretch',
				maxWidth: '640px',
				display: 'grid',
				gap: isReserveService ? 0.5 : 1,
				margin: 0,
				padding: '0.75rem 1rem',
				borderRight: '1px solid rgba(134, 149, 175, 0.15)',
				background: 'rgba(245, 247, 252, 0.50)',
			}}
		>
			{isReserveService ? (
				<>
					<Typography
						variant='body2'
						sx={{ ...body2TextStyle, fontWeight: 600 }}
					>
						Serviço de Reserva
					</Typography>
					<Typography
						variant='body2'
						sx={body2TextStyle}
					>
						Motorista às Ordens
					</Typography>
				</>
			) : (
				<>
					<Typography
						variant='body2'
						sx={body2TextStyle}
					>
						<span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
							{serviceDetail.service?.origin}
						</span>
						<span>
							{serviceDetail.service?.startDateTime
								? dayjs(serviceDetail.service?.startDateTime).format('HH:mm')
								: '--:--'}
						</span>
						<span>
							{serviceDetail.service?.startDateTime
								? dayjs(serviceDetail.service?.startDateTime).format('DD/MM/YYYY')
								: '--/--/----'}
						</span>
					</Typography>
					<Typography
						variant='body2'
						sx={body2TextStyle}
					>
						<span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
							{serviceDetail.service?.destination}
						</span>
						<span>
							{serviceDetail.service?.endDateTime ? dayjs(serviceDetail.service?.endDateTime).format('HH:mm') : '--:--'}
						</span>
						<span>
							{serviceDetail.service?.endDateTime
								? dayjs(serviceDetail.service?.endDateTime).format('DD/MM/YYYY')
								: '--/--/----'}
						</span>
					</Typography>
					<Box
						display='grid'
						alignItems='center'
						sx={{
							gridTemplateColumns: '1fr 34px 72px',
							gap: 1,
							paddingTop: '5px',
							borderTop: '1px solid rgba(134, 149, 175, 0.15)',
						}}
					>
						<Typography
							variant='body2'
							sx={{
								gridColumn: 'span 2',
								display: 'inline-block',
								fontFamily: 'var(--font-family)',
								fontWeight: 400,
								fontSize: '12px',
								lineHeight: '130%',
								letterSpacing: '0.17px',
								maxWidth: '100%',
								color: 'var(--primary)',
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
							}}
						>
							{serviceDetail.service.clientName}
						</Typography>
						<Typography
							variant='body2'
							sx={{
								display: 'inline-block',
								fontFamily: 'var(--font-family)',
								fontWeight: 400,
								fontSize: '12px',
								lineHeight: '130%',
								letterSpacing: '0.17px',
								color: 'var(--primary)',
							}}
						>
							{serviceDetail.service.contract}
						</Typography>
					</Box>
				</>
			)}
			{isTowDriversService && (
				<Box
					sx={{
						paddingTop: '5px',
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						borderTop: '1px solid rgba(134, 149, 175, 0.15)',
					}}
				>
					<Typography
						variant='body2'
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '0.75rem',
							fontWeight: 600,
							lineHeight: 1.3,
							color: 'var(--primary-dark)',
						}}
					>
						Motorista {isSecondaryDriverService ? 'Secundário' : 'Principal'}
					</Typography>
					<Box sx={{ width: '1px', height: '1rem', background: 'rgba(134, 149, 175, 0.15)' }} />
					<Link
						href={`/timesheet/${serviceDetail.mainSecundaryDriverTimeSheetId!}`}
						target='_blank'
						rel='noopener noreferrer'
						underline='none'
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							fontFamily: 'var(--font-family)',
							fontSize: '0.75rem',
							fontWeight: 600,
							lineHeight: 1.3,
							color: 'var(--primary)',
						}}
					>
						{isSecondaryDriverService ? 'Principal' : 'Secundário'}
						<OpenInNewIcon
							fontSize='small'
							color='inherit'
						/>
					</Link>
				</Box>
			)}
			{isContinuousService && (
				<Box sx={{ display: 'grid', paddingBlock: '5px', borderTop: '1px solid rgba(134, 149, 175, 0.15)' }}>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontWeight: 600,
							fontSize: '12px',
							lineHeight: '130%',
							color: 'var(--primary-dark)',
						}}
					>
						Serviço Continuado: Circuito de Turismo
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '130%',
								color: 'var(--primary-dark)',
							}}
						>
							Dia {currentServiceDay}/{totalServiceDays}
						</Typography>
						<Box sx={{ width: '1px', height: '1rem', background: 'rgba(134, 149, 175, 0.15)' }} />
						<Typography
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '130%',
								color: 'var(--primary-dark)',
							}}
						>
							{dayjs(continuedServiceStartDate).format('DD/MM/YYYY')}
							{' - '}
							{dayjs(continuedServiceEndDate).format('DD/MM/YYYY')}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	)
}
